import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import "./PhotoComponent.scss";

import pre1 from "./photo10.png";
import prop1 from "./photo11.png";
import eng1 from "./photo12.png";

import photo1 from "./photo1.png";
import photo2 from "./photo2.png";
import photo3 from "./photo3.png";
import photo4 from "./photo4.png";
import photo5 from "./photo5.png";
import photo6 from "./photo6.png";
import photo7 from "./photo7.png";
import photo8 from "./photo8.png";
import photo9 from "./photo9.png";

const defaultMomentPhotos = [
  photo1,
  photo2,
  photo3,
  photo4,
  photo5,
  photo6,
  photo7,
  photo8,
  photo9,
];

const defaultPhotos = [pre1, prop1, eng1, ...defaultMomentPhotos];

const PhotoComponent = ({
  title = "Moments of Love",
  subtitle = "Holding onto the laughter, the quiet moments, and the little sparks of magic that brought us here… every step, every dream, leading to our forever. ✨💍",
  photos,
  weddingData = null,
}) => {
  const galleryPhotos = useMemo(() => {
    if (Array.isArray(photos) && photos.length > 0) {
      return photos.slice(0, 12).map((photo, index) => ({
        src: typeof photo === "string" ? photo : photo?.src,
        fallback: defaultPhotos[index],
        alt: `Wedding moment ${index + 1}`,
      }));
    }

    const imageGallery = weddingData?.imageGallery;

    if (
      imageGallery &&
      typeof imageGallery === "object" &&
      !Array.isArray(imageGallery)
    ) {
      const uploadedPhotos = [
        imageGallery.signatureBannerImage || pre1,
        imageGallery.rsvpSectionImage || prop1,
        imageGallery.loveNoteImage || eng1,
        ...Array.from({ length: 9 }, (_, index) => {
          return imageGallery.momentsOfLove?.[index] || defaultMomentPhotos[index];
        }),
      ];

      return uploadedPhotos.map((img, index) => ({
        src: img,
        fallback: defaultPhotos[index],
        alt: `Wedding moment ${index + 1}`,
      }));
    }

    if (Array.isArray(imageGallery)) {
      const uploadedPhotos = [
        imageGallery[0]?.image || pre1,
        imageGallery[1]?.image || prop1,
        imageGallery[2]?.image || eng1,
        ...Array.from({ length: 9 }, (_, index) => {
          return imageGallery[index + 3]?.image || defaultMomentPhotos[index];
        }),
      ];

      return uploadedPhotos.map((img, index) => ({
        src: img,
        fallback: defaultPhotos[index],
        alt: `Wedding moment ${index + 1}`,
      }));
    }

    return defaultPhotos.map((img, index) => ({
      src: img,
      fallback: img,
      alt: `Wedding moment ${index + 1}`,
    }));
  }, [photos, weddingData]);

  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [loadedMap, setLoadedMap] = useState({});

  const touchStart = useRef({ x: 0, y: 0, t: 0 });

  useEffect(() => {
    setLoadedMap({});
  }, [galleryPhotos]);

  useEffect(() => {
    if (!open) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  const handleImageLoad = useCallback((key) => {
    setLoadedMap((prev) => ({
      ...prev,
      [key]: true,
    }));
  }, []);

  const handleImageError = useCallback(
    (e, fallback, key) => {
      if (e.target.src !== fallback) {
        e.target.src = fallback;
      }
      handleImageLoad(key);
    },
    [handleImageLoad]
  );

  const openAt = useCallback((i) => {
    setIdx(i);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStart.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };

  const onTouchEnd = (e) => {
    const start = touchStart.current;
    const end = e.changedTouches?.[0];
    if (!end) return;

    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;
    const dt = Date.now() - start.t;

    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) && dt < 600) {
      if (dx < 0) {
        setIdx((n) => (n + 1) % galleryPhotos.length);
      } else {
        setIdx((n) => (n - 1 + galleryPhotos.length) % galleryPhotos.length);
      }
    }
  };

  const lightbox =
    open && galleryPhotos[idx]
      ? createPortal(
          <div className="photoGallery__lightbox" onClick={close}>
            <div
              className="photoGallery__lightboxFrame"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              <img
                src={galleryPhotos[idx].src || galleryPhotos[idx].fallback}
                alt={galleryPhotos[idx].alt}
              />

              <button
                type="button"
                className="photoGallery__close"
                onClick={close}
                aria-label="Close gallery"
              >
                ✕
              </button>

              <button
                type="button"
                className="photoGallery__nav photoGallery__nav--prev"
                onClick={() =>
                  setIdx((n) => (n - 1 + galleryPhotos.length) % galleryPhotos.length)
                }
                aria-label="Previous image"
              >
                ‹
              </button>

              <button
                type="button"
                className="photoGallery__nav photoGallery__nav--next"
                onClick={() => setIdx((n) => (n + 1) % galleryPhotos.length)}
                aria-label="Next image"
              >
                ›
              </button>

              <div className="photoGallery__count">
                {idx + 1} / {galleryPhotos.length}
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <section className="photoGallery">
        <div className="photoGallery__head">
          <h2 className="photoGallery__title">{title}</h2>
          <div className="photoGallery__line" />
          <p className="photoGallery__subtitle">{subtitle}</p>
        </div>

        <div className="photoGallery__grid">
          {galleryPhotos.map((photo, i) => {
            const key = `${photo.src || photo.fallback}-${i}`;
            const isLoaded = !!loadedMap[key];

            return (
              <button
                key={key}
                type="button"
                className={`photoGallery__item photoGallery__item--${i + 1}`}
                onClick={() => openAt(i)}
              >
                <div
                  className={`photoGallery__media ${
                    isLoaded ? "is-loaded" : ""
                  }`}
                >
                  <img
                    src={photo.src || photo.fallback}
                    alt={photo.alt}
                    loading="lazy"
                    decoding="async"
                    onLoad={() => handleImageLoad(key)}
                    onError={(e) => handleImageError(e, photo.fallback, key)}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {lightbox}
    </>
  );
};

export default PhotoComponent;